import { useRouter } from 'next/router';
import { useAppContext } from '../context';
import { useGetAllUsers } from '../user';
import { useHandleApiCall } from '../useHandleApiCall';
import { API_CONSTANTS } from '../../utils/constant';
import { useState, useCallback } from 'react';
// No longer need manual consent/code exchange; we only build a full OAuth URL and let backend handle impersonation
import { generateRandomString, pkceChallengeFromVerifier } from '../../utils/generateCodeChallenge';
import { setToken } from '../../utils/auth';
import { useSnackbar } from 'notistack';
import { axiosInstance } from '../../utils';

export const useUserManagement = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchedText, setSearchedText] = useState('');
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [loginAsUserLoadingId, setLoginAsUserLoadingId] = useState<string | null>(null);
  const [resetPasswordLoadingId, setResetPasswordLoadingId] = useState<string | null>(null);

  const columns = [
    { id: 'email', label: 'Email' },
    { id: 'name', label: 'Username' },
    { id: 'isActive', label: 'Current Status' },
    { id: 'actions', label: 'Actions' },
  ];

  const { push } = useRouter();
  const appCtx = useAppContext() as any;
  const userData = appCtx?.userData as any;
  const isUserDataLoading = appCtx?.isUserDataLoading as boolean | undefined;
  const { enqueueSnackbar } = useSnackbar();

  const handleRedirectToDashboard = () => {
    push('/dashboard');
  };

  const { mutateAsync: getAllUsers, isLoading } = useGetAllUsers();
  const { patch } = useHandleApiCall();
  const updateUserMutation = patch(
    API_CONSTANTS.UPDATE_USER_STATUS.key,
    API_CONSTANTS.UPDATE_USER_STATUS.path
  );

  const fetchPage = useCallback(
    async (opts: { name?: string; page?: number; pageSize?: number }) => {
      const res = await getAllUsers({
        name: opts.name ?? searchedText,
        page: opts.page ?? page,
        pageSize: opts.pageSize ?? rowsPerPage,
      });
      const items = (res as any)?.items ?? [];
      const total = (res as any)?.total ?? items.length;
      setData(items);
      setTotalCount(total);
    },
    [getAllUsers, page, rowsPerPage, searchedText]
  );

  const handleSearchInputChange = useCallback(
    async (val: string) => {
      try {
        setSearchedText(val);
        setPage(0);
        await fetchPage({ name: val, page: 0, pageSize: rowsPerPage });
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },
    [fetchPage, rowsPerPage]
  );

  const handleChangePage = async (event: any, newPage: number) => {
    setPage(newPage);
    await fetchPage({ page: newPage, pageSize: rowsPerPage });
  };

  const handleChangeRowsPerPage = async (event: any) => {
    const next = parseInt(event.target.value);
    setRowsPerPage(next);
    setPage(0);
    await fetchPage({ page: 0, pageSize: next });
  };

  const handleToggleBan = async () => {
    try {
      setIsUpdating(true);
      if (!selectedUser) throw new Error('No user selected');
      const updateData = { id: selectedUser.id, isActive: !selectedUser.isActive };
      const res = await updateUserMutation.mutateAsync(updateData);
      setData((prevData) =>
        prevData.map((user) => (user.id === selectedUser.id ? (res as any) : user))
      );
      handleConfirmationModalClose();
      setIsUpdating(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleConfirmationModalOpen = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleConfirmationModalClose = () => {
    setSelectedUser(null);
    setIsModalOpen(false);
  };

  const handleConfirm = () => {
    handleToggleBan();
  };

  const handleDownloadCSV = () => {
    if (!data || data.length === 0) {
      enqueueSnackbar('No data to download', { variant: 'error' });
      return;
    }

    setIsDownloading(true);

    try {
      const exportableColumns = columns.filter((col) => col.id !== 'actions');

      // With server-side pagination, current page rows are already in `data`
      const visibleRows = data;

      const csvHeaders = exportableColumns.map((col) => col.label).join(',');

      const csvRows = visibleRows.map((row) =>
        exportableColumns
          .map((col) => {
            let value = row[col.id];
            if (col.id === 'isActive') value = value ? 'Active' : 'Blocked';
            if (typeof value === 'string') {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join(',')
      );

      const csvContent = [csvHeaders, ...csvRows].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user_page_${page + 1}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('CSV download failed:', error);
      enqueueSnackbar('Failed to download CSV', { variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleLoginAsUser = async (targetUser) => {
    try {
      setLoginAsUserLoadingId(targetUser?.id);
      if (!userData?.id) return enqueueSnackbar('Admin user not found', { variant: 'error' });
      if (!targetUser?.id) return enqueueSnackbar('Target user not found', { variant: 'error' });

      enqueueSnackbar('Initiating login as user...', { variant: 'info' });

      // Build a fresh OAuth authorization URL (PKCE)
      const codeVerifier = generateRandomString();
      const codeChallenge = await pkceChallengeFromVerifier(codeVerifier);
      // Persist verifier for callback exchange
      window.sessionStorage.setItem('pkce_code_verifier', codeVerifier);
      try {
        window.localStorage.setItem('pkce_code_verifier_bak', codeVerifier);
      } catch {}

      // Generate state and persist
      const stateVal = generateRandomString();
      window.sessionStorage.setItem('oauth_state', stateVal);
      try {
        window.localStorage.setItem('oauth_state_bak', stateVal);
      } catch {}

      // Ask backend to set an impersonation cookie, then redirect back to the OAuth URL
      const params = new URLSearchParams({
        adminId: String(userData.id),
        targetUserId: String(targetUser.id),
        redirect:
          window.location.origin +
          `/oauth2/auth?access_type=offline&code_challenge_method=S256&response_type=code&prompt=login&client_id=${
            process.env.NEXT_PUBLIC_AUTH_CLIENT_ID
          }&code_challenge=${encodeURIComponent(codeChallenge)}&redirect_uri=${encodeURIComponent(
            String(process.env.NEXT_PUBLIC_AUTH_REDIRECT_URL)
          )}&scope=${encodeURIComponent('offline_access openid')}&state=${encodeURIComponent(
            stateVal
          )}`,
      });
      // Proactively clear existing tokens so no API requests are made with the admin token
      try {
        // Remove immediately to prevent axios from attaching stale admin tokens
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('code_verifier');
      } catch {}

      window.location.href = `/api/auth/admin-start-impersonation?${params.toString()}`;
    } catch (error) {
      console.error('Error initiating login as user:', error);
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to login as user';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setLoginAsUserLoadingId(null);
    }
  };

  const handleSendPasswordReset = async (targetUser) => {
    try {
      setResetPasswordLoadingId(targetUser?.id);
      if (!targetUser?.email) return enqueueSnackbar('User email not found', { variant: 'error' });

      const r = await axiosInstance.post('/api/users/admin/user/send-password-reset', {
        email: targetUser.email,
      });
      if (r.status !== 201) {
        throw new Error(r.data?.error || 'Failed to send password reset');
      }
      enqueueSnackbar('Password reset link sent', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to send password reset', { variant: 'error' });
    } finally {
      setResetPasswordLoadingId(null);
    }
  };

  const handleViewSubscriptions = (targetUser) => {
    if (!targetUser?.email) {
      enqueueSnackbar('User email not found', { variant: 'error' });
      return;
    }
    push({ pathname: '/admin/subscription-management', query: { email: targetUser.email } });
  };

  return {
    columns,
    userData,
    isUserDataLoading,
    isLoading,
    handleRedirectToDashboard,
    handleDownloadCSV,
    handleToggleBan,
    handleConfirmationModalOpen,
    handleConfirmationModalClose,
    isModalOpen,
    handleConfirm,
    isUpdating,
    isDownloading,
    selectedUser,
    page,
    rowsPerPage,
    totalCount,
    data,
    handleChangePage,
    handleChangeRowsPerPage,
    searchedText,
    handleSearchInputChange,
    handleLoginAsUser,
    loginAsUserLoadingId,
    handleSendPasswordReset,
    resetPasswordLoadingId,
    handleViewSubscriptions,
    push,
  };
};
